import { Component, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterLink, RouterModule, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { CommonService } from "../../../core/api/services/utility-services/common.service";
import { SessionDto } from "../../../core/models/session/sessiondto";
import { UrlConstants } from "../../../core/constants/url.constants";
import { RoleConstants } from "../../../core/constants/role.constants";
import { MenuConstants } from "../../../core/constants/menu.constants";
import { SidePanelService } from "../../common/side-panel/side-panel.service";
import { FormType } from "../../../core/constants/form-type.constants";
import { PartialFeaturedResource } from "../../../core/models/featuredResources/partial-featured-resource";
import { EventLogReferrerConstants } from "../../../core/constants/event-log-referrer.constants";
import {EventTypeConstants} from '../../../core/constants/event-type.constants';

@Component({
    selector: "app-customer-home",
    standalone: true,
    imports: [CommonModule, RouterModule, RouterLink, TranslatePipe],
    templateUrl: "./customer-home.component.html",
    styleUrl: "./customer-home.component.css",
})
export class CustomerHomeComponent implements OnInit {
    sessionDetails: SessionDto;
    isPartner: boolean;
    partialFeatureResource: PartialFeaturedResource[] = [];
    showFeaturedResource: boolean = false;
    readonly commonService = inject(CommonService);
    readonly translateService = inject(TranslateService);
    readonly router = inject(Router);
    readonly sidePanelService = inject(SidePanelService);

    getCoreCommunicationsWords(): string[] {
        const text = this.translateService.instant("homepage.dashboard.coreCommunications");
        return text.split(/ (.+)/);
    }

    getEducationalMaterialWords(): string[] {
        const text = this.translateService.instant("homepage.dashboard.educationalMaterial");
        return text.split(/ (.+)/);
    }

    getEmptyBoxesCount(): number[] {
        const count = Math.max(0, 4 - this.partialFeatureResource.length);
        return Array(count)
            .fill(0)
            .map((_, i) => i);
    }

    menuItems = [
        // Customers
        {
            path: UrlConstants.myProductsCustomerUrl,
            object: "homepage.customer.myProducts",
            role: RoleConstants.VIEW_CUSTOMER_PRODUCTS_PAGE,
        },
        {
            path: UrlConstants.communicationsCustomerUrl,
            object: "homepage.communications",
            role: RoleConstants.VIEW_CUSTOMER_COMM_PAGE,
        },
        {
            path: UrlConstants.promotionsCustomerUrl,
            object: "homepage.promotions",
            role: RoleConstants.VIEW_CUSTOMER_PROMO_PAGE,
        },
        {
            path: UrlConstants.reportsCustomerUrl,
            object: "homepage.reports",
            role: RoleConstants.VIEW_CUSTOMER_REPORTS,
        },
        {
            path: UrlConstants.trainingCustomerUrl,
            object: "homepage.training",
            role: RoleConstants.VIEW_CUSTOMER_TRAINING_PAGE,
        },
        {
            path: UrlConstants.healthFairCustomerUrl,
            object: "homepage.healthFair",
            role: RoleConstants.VIEW_CUSTOMER_HEALTH_FAIR_PAGE,
        },
        {
            path: UrlConstants.contactUsCustomerUrl,
            object: "homepage.contactUs",
            role: RoleConstants.VIEW_CUSTOMER_CONTACT_US_PAGE,
        },

        //partners
        {
            path: UrlConstants.overviewPartnerUrl,
            object: "homepage.overview",
            role: RoleConstants.VIEW_PARTNER_OVERVIEW_PAGE,
        },
        {
            path: UrlConstants.productsPartnerUrl,
            object: "homepage.partner.products",
            role: RoleConstants.VIEW_PARTNER_PRODUCTS_PAGE,
        },
        {
            path: UrlConstants.salesInfoPartnerUrl,
            object: "homepage.sales",
            role: RoleConstants.VIEW_PARTNER_SALES_PAGE,
        },
        {
            path: UrlConstants.communicationsPartnerUrl,
            object: "homepage.communications",
            role: RoleConstants.VIEW_PARTNER_COMM_PAGE,
        },
        {
            path: UrlConstants.promotionsPartnerUrl,
            object: "homepage.promotions",
            role: [RoleConstants.VIEW_PARTNER_PROMO_PAGE, RoleConstants.PARTNER_ADMIN],
        },
        {
            path: MenuConstants.ACCOUNTS,
            object: "homepage.accounts",
            role: [
                RoleConstants.VIEW_PARTNER_ACCOUNTS,
                RoleConstants.VIEW_PARTNER_SALES_OFFICES,
                RoleConstants.VIEW_PARTNER_USERS,
            ],
        },
        { path: UrlConstants.reportsPartnerUrl, object: "homepage.reports", role: RoleConstants.VIEW_PARTNER_REPORTS },
        {
            path: UrlConstants.contactUsPartnerUrl,
            object: "homepage.contactUs",
            role: RoleConstants.VIEW_PARTNER_CONTACT_US_PAGE,
        },
        {
            path: UrlConstants.clientFeedbackPartnerUrl,
            object: "homepage.clientFeedback",
            role: RoleConstants.VIEW_PARTNER_FEEDBACK_PAGE,
        },
    ];

    ngOnInit() {
        let item = this.menuItems.find(
            (menuItem) =>
                menuItem.path === UrlConstants.productsPartnerUrl &&
                menuItem.role === RoleConstants.VIEW_PARTNER_PRODUCTS_PAGE
        );
        this.commonService.sessiondetails?.subscribe((res) => {
            this.sessionDetails = res;
            this.isPartner = this.accessible(RoleConstants.PARTNER);
            if (item && this.isPartner) {
                if (this.sessionDetails.hasProgramModels) {
                    item.object = "homepage.programs";
                    item.path = UrlConstants.programsPartnerUrl;
                }
            }
        });

        this.commonService.getPartialFeatureResources().subscribe({
            next: (res) => {
                this.partialFeatureResource = res.partialFeatureResources;
                this.showFeaturedResource = res.showFeaturedResource;
            },
            error: (err) => {
                console.error("Error fetching promotional content:", err);
                this.partialFeatureResource = [];
                this.showFeaturedResource = false;
            },
        });
    }

    accessible(role: string): boolean {
        return this.sessionDetails && this.sessionDetails.userRoles?.includes(role);
    }

    displayMenu(menuitem: any): boolean {
        if (menuitem.path === MenuConstants.ACCOUNTS) {
            return (
                this.accessible(RoleConstants.VIEW_PARTNER_ACCOUNTS) ||
                this.accessible(RoleConstants.VIEW_PARTNER_SALES_OFFICES) ||
                this.accessible(RoleConstants.VIEW_PARTNER_USERS)
            );
        } else if (typeof menuitem.role === "string") {
            return this.accessible(menuitem.role);
        } else {
            let accessibleItemCount = 0;
            menuitem.role.forEach((item: string) => {
                if (this.accessible(item)) {
                    accessibleItemCount++;
                }
            });
            return menuitem.role.length === accessibleItemCount;
        }
    }

    isEnglishLanguage(): boolean {
        return (
            this.translateService.currentLang === "en_US" ||
            (this.translateService.currentLang === undefined && this.translateService.defaultLang === "en_US")
        );
    }

    navigateToEvents(): void {
        this.router.navigate(["/events/my-events", { referrer: EventLogReferrerConstants.HOMEPAGE }]);
    }

    navigateToDashboard(): void {
        this.router.navigate([UrlConstants.dashboardCustomerUrl, { referrer: EventLogReferrerConstants.HOMEPAGE }]);
    }

    navigateToTraining(): void {
        this.sidePanelService.open({
            formType: FormType.TRAINING,
            title: "",
            referrer: EventLogReferrerConstants.HOMEPAGE,
        });
    }

    navigateToHealthFair(): void {
        this.sidePanelService.open({
            formType: FormType.HEALTH_FAIR,
            title: "",
            referrer: EventLogReferrerConstants.HOMEPAGE,
        });
    }

    navigateToCommunications(): void {
        this.router.navigate(["/customer/communications", { referrer: EventLogReferrerConstants.HOMEPAGE }]);
    }

    navigateToPromotions(): void {
        this.router.navigate(["/customer/promotions", { referrer: EventLogReferrerConstants.HOMEPAGE }]);
    }
    openPromotionalContent(resource: PartialFeaturedResource): void {
        if (resource && resource.url) {
            this.commonService.logEvent(EventTypeConstants.USER_CLICKED_HOMEPAGE_AD, `User clicked a homepage advertisement`, resource.text);
            window.open(resource.url, "_blank");
        }
    }

    getCardClass(index: number): string {
        const classes = [
            "helpful-resource-card-cyan",
            "helpful-resource-card-light-blue",
            "helpful-resource-card-dark-blue",
            "helpful-resource-card-mint",
        ];
        return classes[index % classes.length];
    }

    protected readonly RoleConstants = RoleConstants;
}
